import { ReferenceType } from '@floating-ui/react-dom'
import { last } from 'lodash'
import { computed, makeAutoObservable, observable } from 'mobx'
import { SUPPORTED_FONTS } from 'prose-core'
import React, { RefObject } from 'react'

import { MONOSPACE_FONTS } from 'common/Prose/constants'
import {
  AvailableWeights,
  WeightValue,
} from 'common/Prose/Toolbar/components/Format/components/MainStylePane/components/FontWeight/util'
import { FontFamilyType } from 'utils/fontUtils'

type WindowClickSignaler = (e: MouseEvent) => boolean /* isMine */
export function getStillInsideWindowClickSignaler<T extends HTMLElement>(ref: RefObject<T>) {
  return (e: MouseEvent) => !!ref.current?.contains(e.target as Node)
}

export class FontFamilyStore {
  searchValue: string = ''
  currentFontFamily?: FontFamilyType
  currentFontWeight?: WeightValue
  hardClosed: Boolean = true
  showingFontFamilyDropdown: Boolean = false
  fontFamilyAnchor: ReferenceType | null = null
  fontWeightAnchor: ReferenceType | null = null
  windowClickIgnorers = new Set<WindowClickSignaler>()
  windowClickHandlers = new Set<WindowClickSignaler>()
  selectingLevel: number = 0
  _activeFamilies?: FontFamilyType[]
  _activeWeights?: WeightValue[]
  _hasCodeBlock?: boolean
  constructor() {
    makeAutoObservable(this, {
      fontFamilyAnchor: observable,
      fontWeightAnchor: observable,
      currentFontFamily: observable,
      isCurrentFontFamilyActive: computed,
      currentFontWeight: observable,
      showingFontFamilyDropdown: observable,
      activeFontFamilies: computed,
      showingFontWeightDropdown: computed,
      filteredFontList: computed,
      contextFonts: computed,
      selectedFontWeights: computed,
      firstActiveFontDefinition: computed,
    })
  }
  get activeFontFamilies(): Set<FontFamilyType> {
    return new Set(SUPPORTED_FONTS.filter((fontName) => this._activeFamilies?.includes(fontName)))
  }
  get isCurrentFontFamilyActive(): boolean {
    return this.currentFontFamily != null && this.activeFontFamilies.has(this.currentFontFamily)
  }
  get activeFontWeights(): Set<WeightValue> {
    if (!this.selectedFontWeights) {
      return new Set()
    }

    return new Set(
      this.selectedFontWeights.flatMap((fontWeight) => (this._activeWeights?.includes(fontWeight) ? [fontWeight] : [])),
    )
  }
  get showingFontWeightDropdown() {
    return this.currentFontFamily != null
  }
  get filteredFontList() {
    return this.contextFonts.filter(
      (fontName) =>
        fontName.toLowerCase().includes(this.searchValue.toLowerCase()) &&
        SUPPORTED_FONTS.find((defFontName) => defFontName === fontName),
    )
  }
  get contextFonts() {
    return this._hasCodeBlock ? MONOSPACE_FONTS : SUPPORTED_FONTS
  }
  get selectedFontWeights() {
    if (this.currentFontFamily) {
      return AvailableWeights[this.currentFontFamily]
    }
    return undefined
  }
  get firstActiveFontDefinition() {
    return SUPPORTED_FONTS.find((fontName) => this._activeFamilies?.includes(fontName)) || SUPPORTED_FONTS[0]
  }
  set activeFamilies(val: typeof this._activeFamilies) {
    this._activeFamilies = val
  }
  set activeWeights(val: typeof this._activeWeights) {
    this._activeWeights = val
  }
  set hasCodeBlock(val: typeof this._hasCodeBlock) {
    this._hasCodeBlock = val
  }
  setFontFamilyAnchor = (ref: ReferenceType | null) => {
    this.fontFamilyAnchor = ref
  }
  setFontWeightAnchor = (ref: ReferenceType | null) => {
    this.fontWeightAnchor = ref
  }
  setCurrentFontFamily(fontFamily: FontFamilyType) {
    this.currentFontFamily = fontFamily
    this.currentFontWeight = undefined
  }
  setCurrentFontWeight(fontWeight: WeightValue) {
    this.currentFontWeight = fontWeight
  }
  selectNext = () => {
    if (!this.currentFontFamily) {
      this.selectingLevel = 0
    }
    // Font Family
    if (this.selectingLevel === 0) {
      const fonts = this.filteredFontList
      if (!this.currentFontFamily) {
        this.currentFontFamily = fonts[0]
        return
      }
      for (let i = 0; i < fonts.length; i++) {
        if (fonts[i] === this.currentFontFamily) {
          this.currentFontFamily = fonts[i + 1] ?? fonts[0]
          return
        }
      }
    }
    // Font Weight
    if (this.selectingLevel === 1 && this.selectedFontWeights) {
      const weights = this.selectedFontWeights
      if (!this.currentFontWeight) {
        this.currentFontWeight = weights[0]
        return
      }
      for (let i = 0; i < weights.length; i++) {
        if (weights[i] === this.currentFontWeight) {
          this.currentFontWeight = weights[i + 1] ?? weights[0]
          return
        }
      }
    }
  }
  selectPrevious = () => {
    if (!this.currentFontFamily) {
      this.selectingLevel = 0
    }
    // Font Family
    if (this.selectingLevel === 0) {
      const fonts = this.filteredFontList
      if (!this.currentFontFamily) {
        this.currentFontFamily = fonts[0]
        return
      }
      for (let i = 0; i < fonts.length; i++) {
        if (fonts[i] === this.currentFontFamily) {
          this.currentFontFamily = fonts[i - 1] ?? fonts[fonts.length]
          break
        }
      }
    }
    // Font Weight
    if (this.selectingLevel === 1 && this.selectedFontWeights) {
      const weights = this.selectedFontWeights
      if (!this.currentFontWeight) {
        this.currentFontWeight = '400'
        return
      }
      for (let i = 0; i < weights.length; i++) {
        if (weights[i] === this.currentFontWeight) {
          this.currentFontWeight = weights[i - 1] ?? last(weights)
          return
        }
      }
    }
  }
  goDeeper = () => {
    this.selectingLevel = 1
    this.currentFontWeight = '400'
  }
  goShallower = () => {
    this.selectingLevel = 0
    this.currentFontWeight = undefined
  }
  registerWindowClickSignaler = (wcs: WindowClickSignaler) => {
    this.windowClickHandlers.add(wcs)
  }
  unregisterWindowClickSignaler = (wcs: WindowClickSignaler) => {
    this.windowClickHandlers.delete(wcs)
  }
  registerWindowClickIgnorer = (wcs: WindowClickSignaler) => {
    this.windowClickIgnorers.add(wcs)
  }
  unregisterWindowClickIgnorer = (wcs: WindowClickSignaler) => {
    this.windowClickIgnorers.delete(wcs)
  }
  handleWindowClick = (e: MouseEvent) => {
    for (const handler of this.windowClickIgnorers) {
      if (handler(e)) return
    }
    for (const handler of this.windowClickHandlers) {
      if (handler(e)) return
    }
    this.reset()
  }
  hardOpen = () => {
    this.hardClosed = false
    this.showingFontFamilyDropdown = true
  }
  softOpen = () => {
    if (this.hardClosed) return
    this.showingFontFamilyDropdown = true
  }
  selectAndClose = (closingFunction: (fontFamily: FontFamilyType, weight: WeightValue) => void) => {
    if (!this.currentFontFamily) {
      this.selectNext()
    }
    if (this.currentFontFamily) {
      const fontFamily = this.currentFontFamily
      const fontWeight = this.currentFontWeight || '400'
      this.hardClose()
      this.reset()
      closingFunction(fontFamily, fontWeight)
    }
  }
  softClose = () => {
    this.softReset()
  }
  hardClose = () => {
    this.searchValue = ''
    this.hardClosed = true
    this.showingFontFamilyDropdown = false
    this.currentFontWeight = undefined
    this.softReset()
  }
  reset = () => {
    this.searchValue = ''
    this.hardClosed = false
    this.softReset()
  }
  private softReset = () => {
    this.selectingLevel = 0
    this.currentFontFamily = undefined
    this.currentFontWeight = undefined
    this.showingFontFamilyDropdown = false
  }
}

export const FontFamilyContext = React.createContext<FontFamilyStore | null>(null)
